import React, { useState } from 'react';
import { Instagram, Youtube, Twitter, Linkedin, Image as ImageIcon, Check, Scissors, ChevronDown, ChevronUp } from 'lucide-react';

export interface ImagePreset {
  id: string;
  name: string;
  category: 'Instagram' | 'YouTube' | 'Twitter/X' | 'LinkedIn' | 'Passport/ID';
  width: number;
  height: number;
  icon: React.ReactNode;
}

export const PRESETS: ImagePreset[] = [
  {
    id: 'ig-square',
    name: 'Instagram Square',
    category: 'Instagram',
    width: 1080,
    height: 1080,
    icon: <Instagram className="w-4 h-4 text-pink-500" />,
  },
  {
    id: 'ig-story',
    name: 'Instagram Story/Reel',
    category: 'Instagram',
    width: 1080,
    height: 1920,
    icon: <Instagram className="w-4 h-4 text-pink-500" />,
  },
  {
    id: 'ig-portrait',
    name: 'Instagram Portrait',
    category: 'Instagram',
    width: 1080,
    height: 1350,
    icon: <Instagram className="w-4 h-4 text-pink-500" />,
  },
  {
    id: 'yt-thumb',
    name: 'YouTube Thumbnail',
    category: 'YouTube',
    width: 1280,
    height: 720,
    icon: <Youtube className="w-4 h-4 text-red-500" />,
  },
  {
    id: 'yt-banner',
    name: 'YouTube Banner',
    category: 'YouTube',
    width: 2560,
    height: 1440,
    icon: <Youtube className="w-4 h-4 text-red-500" />,
  },
  {
    id: 'tw-post',
    name: 'Twitter/X Post',
    category: 'Twitter/X',
    width: 1200,
    height: 675,
    icon: <Twitter className="w-4 h-4 text-sky-400" />,
  },
  {
    id: 'tw-header',
    name: 'Twitter/X Header',
    category: 'Twitter/X',
    width: 1500,
    height: 500,
    icon: <Twitter className="w-4 h-4 text-sky-400" />,
  },
  {
    id: 'li-profile',
    name: 'LinkedIn Profile',
    category: 'LinkedIn',
    width: 400,
    height: 400,
    icon: <Linkedin className="w-4 h-4 text-blue-600" />,
  },
  {
    id: 'li-banner',
    name: 'LinkedIn Banner',
    category: 'LinkedIn',
    width: 1584,
    height: 396,
    icon: <Linkedin className="w-4 h-4 text-blue-600" />,
  },
  {
    id: 'passport-us',
    name: 'US Passport (2×2")',
    category: 'Passport/ID',
    width: 600,
    height: 600,
    icon: <ImageIcon className="w-4 h-4 text-emerald-500" />,
  },
  {
    id: 'passport-eu',
    name: 'EU/UK Passport (35×45mm)',
    category: 'Passport/ID',
    width: 413,
    height: 531,
    icon: <ImageIcon className="w-4 h-4 text-emerald-500" />,
  },
];

interface PresetSelectorProps {
  activePresetId: string | null;
  onSelectPreset: (preset: ImagePreset) => void;
  onAdjustCrop?: (preset: ImagePreset) => void;
}

export function PresetSelector({ activePresetId, onSelectPreset, onAdjustCrop }: PresetSelectorProps) {
  // Pre-folded / collapsed by default unless a preset is active
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const activePreset = PRESETS.find((p) => p.id === activePresetId);

  return (
    <div className="space-y-2">
      {/* Prefolded Accordion Header Trigger */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          {isOpen ? <ChevronUp className="w-4 h-4 text-indigo-500" /> : <ChevronDown className="w-4 h-4 text-indigo-500" />}
          <span>Quick Social & ID Presets</span>
          {activePreset && (
            <span className="normal-case font-semibold text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
              {activePreset.name}
            </span>
          )}
        </button>

        {activePreset && onAdjustCrop && (
          <button
            type="button"
            onClick={() => onAdjustCrop(activePreset)}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-3 py-1 rounded-lg shadow-sm flex items-center gap-1.5 transition-all hover:scale-105"
          >
            <Scissors className="w-3.5 h-3.5" />
            <span>Adjust Crop</span>
          </button>
        )}
      </div>

      {/* Accordion Body Content */}
      {isOpen && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-2 animate-in fade-in duration-200">
          {PRESETS.map((preset) => {
            const isSelected = activePresetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => onSelectPreset(preset)}
                className={`p-2.5 rounded-xl border text-left transition-all relative flex flex-col justify-between ${
                  isSelected
                    ? 'border-indigo-600 bg-indigo-50/80 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 ring-2 ring-indigo-500/30'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-between gap-1.5 mb-1.5">
                  <span className="p-1 rounded-md bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                    {preset.icon}
                  </span>
                  {isSelected && (
                    <span className="w-4 h-4 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">
                      <Check className="w-2.5 h-2.5" />
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold line-clamp-1 leading-snug">{preset.name}</p>
                  <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                    {preset.width}×{preset.height}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
